import React, { CSSProperties } from 'react';

import SignUpBanner from '../components/Banners/SignUp';
import SignUpBannerMini from '../components/Banners/SignUpMini';
import { Layout } from '../components/common';
import { MetaData } from '../components/common/meta';
import { FinancialTools } from '../components/FinancialTools/FinancialTools';
import { PostAuthor } from '../components/Post/Author';
import { PostAuthorDescription } from '../components/Post/Author/Description';
import { IPostCard } from '../components/Post/Card';
import { PopularPosts } from '../components/Post/Popular';
import { PostSocialLinks } from '../components/Post/SocialLinks';
import ProgressBarDesktop from '../components/ProgressBar/ProgressBarDesktop';
import RenderOnVisible from '../components/RenderOnVisible';
import {
  renderGhostAuthorAside,
  renderGhostChatBubbles,
  renderGhostImageFloat,
  renderGhostInlineTOC,
} from '../utils/ghostSnippets';
import { GhostRelatedArticlesCarousel } from '../utils/GhostSnippets/RelatedArticlesCarousel';
import { GhostSuggestedElements } from '../utils/GhostSnippets/SuggestedElements';
import { lazyLoading } from '../utils/lazyLoading';
import trustAllScripts from '../utils/trustAllScripts';

import stylePost from '../styles/pages/post.scss';
import style from '../styles/pages/post.scss';

import { Tags } from '@tryghost/helpers-gatsby';
import cn from 'classnames';
import { graphql, Link } from 'gatsby';
import { Helmet } from 'react-helmet';
import { Sticky, StickyContainer } from 'react-sticky';

interface IPostProps {
  data: {
    ghostPost: IPostCard;
    allGhostPost: { edges: { node: IPostCard }[] };
    allGhostAuthor: {
      edges: {
        node: {
          name: string;
          slug: string;
          profile_image: string;
          bio: string;
        };
      }[];
    };
    allAuthorsBioFullJson: { edges: { node: { slug: string; bio: string } }[] };
    // allGhostPostFeatured: any;
    // allGhostPageFeatured: any;
    article1: any;
    article2: any;
    article3: any;
  };
  location: any;
}

/**
 * Single post view (/:slug)
 *
 * This file renders a single post and loads all the content.
 *
 */

// RenderOnVisible since content not visible on most screens
// Extracted since onRouteUpdate from gatsby-browser.js runs before mounted
const Content = ({
  content,
  allGhostAuthor,
}: {
  content: string;
  allGhostAuthor: {
    edges: {
      node: {
        name: string;
        slug: string;
        profile_image: string;
        bio: string;
      };
    }[];
  };
}) => {
  React.useEffect(() => {
    trustAllScripts();

    renderGhostImageFloat();

    renderGhostInlineTOC();

    renderGhostChatBubbles(allGhostAuthor.edges);

    renderGhostAuthorAside(allGhostAuthor.edges);

    lazyLoading();
  }, []);

  if (!content) {
    return null;
  }

  return (
    <section
      className={cn(
        'load-external-scripts',
        stylePost.singleArticleFullContent,
        stylePost.singleArticleBodyContent
      )}
      dangerouslySetInnerHTML={{
        __html: content
          // lazy load images
          .replace(/<img/gm, '<img loading="lazy"')
          // open external links in new tab
          .replace(
            /<a[\s]+href="(?!(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?(((?!go))[a-z0-9]*)([\-\.]*thinksaveretire+)\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?)/gm,
            '<a target="_blank" href="'
          ),
      }}
    />
  );
};

const Post = React.memo(({ data, location }: IPostProps) => {
  const {
    allGhostAuthor,
    allAuthorsBioFullJson,
    ghostPost,
    article1,
    article2,
    article3,
    // allGhostPostFeatured,
    // allGhostPageFeatured,
  } = data;
  const post = ghostPost;

  React.useEffect(() => {
    post.tags.push(
      post.tags.splice(
        post.tags.findIndex((tag) => tag && tag.slug === 'guest-posts'),
        1
      )[0]
    );

    // Note, some authors have no full bio.
    const authorsBioFull = allAuthorsBioFullJson.edges.find(({ node }) => {
      return node.slug === post.primary_author.slug;
    });

    if (authorsBioFull) {
      post.primary_author.bio = authorsBioFull.node.bio;
    }
  }, []);

  // const allPostsAndPagesFeatured = allGhostPostFeatured.edges.concat(allGhostPageFeatured.edges);

  return (
    <>
      <MetaData data={data} location={location} />
      <Helmet>
        <style type="text/css">{`${post.codeinjection_styles}`}</style>
      </Helmet>
      <Layout postTitle={post.title} mainClass={style.main} isArticle>
        <StickyContainer className={stylePost.sticky}>
          <Sticky>
            {({ style, isSticky }: { style: CSSProperties; isSticky: boolean }) => (
              <div style={{ ...style }}>
                <RenderOnVisible>
                  <ProgressBarDesktop
                    className={isSticky ? stylePost.stickyPrograssBar : ''}
                    title={post.title}
                    content={post.html}
                  />
                </RenderOnVisible>
              </div>
            )}
          </Sticky>
          <div className="container">
            <div className="row">
              <article className={cn('col-12 col-lg-8', stylePost.singleArticle)}>
                <section className={stylePost.singleArticleFullContent}>
                  {post.tags && (
                    <Tags
                      classes={cn(stylePost.singleArticleTags, style.featuredTag)}
                      post={post}
                      visibility="public"
                      limit={1}
                      permalink="/tag/:slug/"
                    />
                  )}
                  <h1 className={stylePost.singleArticleTitle}>{post.title}</h1>
                  {post.custom_excerpt ? (
                    <p className={stylePost.singleArticleExcerpt}>{post.custom_excerpt}</p>
                  ) : null}
                  <div className="d-md-flex justify-content-between">
                    <PostAuthor
                      publishedAt={post.published_at}
                      primary_author={post.primary_author}
                      post={post}
                    />
                    <div className={cn('d-none d-md-block', style.singleArticleAuthorLinks)}>
                      <PostSocialLinks feature_image={post.feature_image} />
                    </div>
                  </div>
                  <p className={style.disclaimerText}>
                    To keep this blog ad-free, this post may contain affiliate links and/or paid
                    placement.{' '}
                    <Link to="/disclosure/">Click here to read our full disclosure.</Link>
                  </p>
                </section>
                {post.feature_image ? (
                  <figure className={stylePost.singleArticleFeatureImage}>
                    <img className="lazy" data-src={post.feature_image} alt={post.title} />
                    <div className={cn('d-md-none', style.singleArticleAuthorLinks)}>
                      <PostSocialLinks feature_image={post.feature_image} />
                    </div>
                  </figure>
                ) : null}

                {/* The main post content */}
                <Content content={post.html} allGhostAuthor={allGhostAuthor} />
                <section className={stylePost.singleArticleFullContent}>
                  <PostSocialLinks feature_image={post.feature_image} />
                  {post.tags && (
                    <div className={stylePost.singleArticleFooterTags}>
                      <Tags
                        classes={stylePost.singleArticleTags}
                        post={post}
                        visibility="public"
                        separator=""
                        permalink="/tag/:slug/"
                      />
                    </div>
                  )}
                </section>
                <PostAuthorDescription primary_author={post.primary_author} />
                <RenderOnVisible>
                  <GhostRelatedArticlesCarousel ghostPost={ghostPost} />
                  <GhostSuggestedElements ghostPost={ghostPost} />
                </RenderOnVisible>
                <RenderOnVisible>
                  <Helmet>
                    <script defer src={process.env.COMMENTO_SCRIPT_SRC} data-no-fonts="true" />
                  </Helmet>
                  <section className={stylePost.commentsContainer}>
                    <h3 className={stylePost.commentsHeading}>Comments</h3>
                    <div id="commento" />
                  </section>
                </RenderOnVisible>
              </article>
              <section className={cn('d-none d-lg-block col-lg-4')}>
                <Sticky>
                  {({ style: stickyStyle }: { style: CSSProperties; isSticky: boolean }) => (
                    <div style={{ ...stickyStyle }}>
                      <div className={style.sidebar}>
                        <SignUpBannerMini />
                        <h3>More Reading</h3>
                        <PopularPosts
                          posts={
                            article1?.edges?.concat(article2?.edges).concat(article3?.edges) || []
                          }
                        />
                        <FinancialTools />
                      </div>
                    </div>
                  )}
                </Sticky>
              </section>
            </div>
          </div>
        </StickyContainer>
        <RenderOnVisible>
          <SignUpBanner />
        </RenderOnVisible>
      </Layout>
    </>
  );
});

export default Post;

export const postQuery = graphql`
  query($slug: String!) {
    ghostPost(slug: { eq: $slug }) {
      ...GhostPostFields
    }
    allGhostAuthor {
      edges {
        node {
          name
          slug
          bio
          profile_image
        }
      }
    }
    allAuthorsBioFullJson {
      edges {
        node {
          bio
          slug
        }
      }
    }
    article1: allGhostPage(filter: { slug: { regex: "/^(build-wealth-guide)$/" } }, limit: 1) {
      edges {
        node {
          title
          slug
          feature_image
          primary_author {
            name
          }
        }
      }
    }
    article2: allGhostPost(filter: { slug: { regex: "/^(budget-happiness)$/" } }, limit: 1) {
      edges {
        node {
          title
          slug
          feature_image
          primary_author {
            name
          }
        }
      }
    }
    article3: allGhostPage(filter: { slug: { regex: "/^(side-hustles-make-money)$/" } }, limit: 1) {
      edges {
        node {
          title
          slug
          feature_image
          primary_author {
            name
          }
        }
      }
    }
    allGhostPageFeatured: allGhostPage(
      sort: { order: DESC, fields: [published_at, featured] }
      filter: { featured: { eq: true } }
      limit: 3
    ) {
      edges {
        node {
          slug
          feature_image
          featured
          title
          published_at
          primary_author {
            name
          }
          slug
        }
      }
    }
  }
`;
